import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Observable, timer } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { ApiService, CategoryWithProducts, ProductResponse } from './api.service';
import { AnalyticsService } from './analytics.service';
import {
  CartSupplement,
  RawCartSupplement,
  SupplementPayload,
  normalizeCartSupplements,
  supplementPayload
} from '../models/supplement.model';
import { cartLineKey, cartLineTotal } from '../shared/supplement-pricing';
import { productCartLines } from '../shared/supplement-tile-routing';
import { ITEM_NOTE_META_KEY, itemNoteFromMeta, mergeItemNotes, normalizeItemNote } from '../shared/item-note';
import { OrderTip, TipChoice, parseOrderTip } from '../shared/order-tip';
import {
  DeliveryLocation,
  DeliveryQuote,
  parseDeliveryQuote,
  parseOrderDeliveryFee,
  parseOrderDeliveryLocation,
} from '../shared/order-delivery';

export interface CartItem {
  /**
   * The PRODUCT id — deliberately still named `id`, and deliberately NOT the line's identity.
   * Once add-ons exist the same product can sit in the cart several times over, so every lookup
   * and mutation below goes through `cartLineKey(id, supplements)` instead. Renaming this would
   * churn every consumer for no gain.
   */
  id: number;
  name: string;
  /** Unit price of the product ALONE — add-ons are priced separately, see `cartLineTotal`. */
  price: number;
  quantity: number;
  image: string;
  images?: any;
  meta_data?: any;
  sku: string;
  regular_price: string, 
  categories?: [];
  /**
   * The add-ons chosen on THIS line. Absent on WooCommerce lines, on lines the guest left plain,
   * and on every cart saved to localStorage before this field existed — which is why absent and
   * empty mean the same thing and no migration is needed: the key of such a line degrades to
   * `"<productId>|"`, exactly the identity it already had.
   */
  supplements?: CartSupplement[];
  /**
   * The guest's note on THIS line, already normalized (see `normalizeItemNote`). Absent or empty
   * means no note — and, like `supplements`, carts saved before the field existed simply lack it.
   *
   * ⚠️ Deliberately NOT part of `cartLineKey`: adding another unit of the same product + add-ons
   * raises this line's quantity and keeps its note, and an add-on change carries it to the new key.
   * Every spread of a line (`{ ...item, quantity }`) keeps it for free; anything that builds a line
   * from scratch has to copy it by hand.
   */
  note?: string;
}

export interface UnavailableProductEvent {
  productId: number;
  productName: string;
  from?: string;
  to?: string;
}

/** A code that was applied and is not any more, as the order response reported it. */
export interface PromoCodeRefusal {
  reason: string;
  /** The code's minimum, sent only with `promo_code_minimum_not_met`. */
  minimumOrderAmount?: number;
}

interface SyncOperation {
  id: string;
  payload: any;
  timestamp: number;
  retryCount: number;
  /**
   * Also zero every server row the local cart no longer has. Queued once after edits were
   * dropped — see reconcileAfterDroppedEdits().
   */
  reconcile?: boolean;
}

/**
 * How one sync went, and what has to happen once its guard is released: send the edits that
 * arrived while it was on the wire (or the lines of a request refused only for its tip), or
 * zero the server rows of edits it had to drop. See processQueue().
 */
interface SyncOutcome {
  ok: boolean;
  next: 'none' | 'now' | 'reconcile';
}

/**
 * One line of the outbound `line_items` array.
 *
 * `supplements` is present ONLY when the line actually has some, so a venue without add-ons posts
 * a payload byte-identical to the one it posted before this feature existed.
 */
interface LineItemPayload {
  product_id: number;
  quantity: number;
  subtotal: string;
  total: string;
  meta_data: Array<{ key: string; value: string }>;
  supplements?: SupplementPayload[];
  id?: number;
}

/** The (untyped) order rows the server sends back, in the shape this service reads them. */
interface ServerLineItem {
  id?: number;
  product_id?: number;
  quantity?: number;
  supplements?: RawCartSupplement[];
  meta_data?: Array<{ key?: string; value?: unknown }>;
}

@Injectable({
  providedIn: 'root'
})

export class CartService {
  private cartItems = new BehaviorSubject<CartItem[]>([]);
  cartItems$ = this.cartItems.asObservable();

  private _total = new BehaviorSubject<number>(0);
  total$ = this._total.asObservable();

  /**
   * The applied promo code and what it is worth, as the SERVER last reported them.
   *
   * Both are driven by the `updateOrder` response rather than computed here. The server is
   * the only authority on what an order costs: it re-validates the code on every sync, and
   * a code that has expired since it was applied comes back refused with the order re-priced
   * at gross. A locally-computed discount would keep showing the old, lower figure.
   */
  private _appliedPromoCode = new BehaviorSubject<string | null>(null);
  appliedPromoCode$ = this._appliedPromoCode.asObservable();

  private _discountTotal = new BehaviorSubject<number>(0);
  discountTotal$ = this._discountTotal.asObservable();

  /**
   * Emits when the server REFUSES a code that was already applied — meaning the order has
   * been re-priced at gross and the total has gone UP.
   *
   * ⚠️ Not the same event as a code being rejected at the moment the guest types it. That
   * one is an ordinary field error on the validate endpoint. This one is a change to an
   * order the guest has already seen a price for, so it has to be surfaced and confirmed
   * rather than quietly corrected.
   */
  private _promoCodeRefused = new Subject<PromoCodeRefusal>();
  promoCodeRefused$ = this._promoCodeRefused.asObservable();

  /**
   * The tip on the order, as the SERVER last reported it — null for no tip.
   *
   * Like the promo, never computed here. The guest only picks (a preset or an amount); the
   * server works out the figure from the items after any discount, keeps it in step as the
   * cart changes, and includes it in `total`.
   */
  private _tip = new BehaviorSubject<OrderTip | null>(null);
  tip$ = this._tip.asObservable();

  /** Emits the error code when the server refuses a tip (e.g. the venue changed its presets). */
  private _tipRefused = new Subject<string>();
  tipRefused$ = this._tipRefused.asObservable();

  /**
   * The delivery on the order, as the SERVER last reported it.
   *
   * `deliveryFee$` is our shipping line: null for no delivery line, 0 for free delivery.
   * `deliveryQuote$` is `feather_extras.delivery` — the status and the messages' figures —
   * null while the order has no location. Like the tip, never computed here: the server
   * matches the pin to an area, re-prices it on every sync and includes it in `total`.
   */
  private _deliveryFee = new BehaviorSubject<number | null>(null);
  deliveryFee$ = this._deliveryFee.asObservable();

  private _deliveryQuote = new BehaviorSubject<DeliveryQuote | null>(null);
  deliveryQuote$ = this._deliveryQuote.asObservable();

  /** True from the moment a location is handed over until a response that carried it lands. */
  private _deliveryPricing = new BehaviorSubject<boolean>(false);
  deliveryPricing$ = this._deliveryPricing.asObservable();

  /**
   * Emits when a sync carrying a location did not go through: the server's `delivery_*` code
   * (HTTP 422), or `'sync_failed'` for anything else (offline, 5xx). Either way pricing is
   * over and the guest can confirm again.
   */
  private _deliveryRefused = new Subject<string>();
  deliveryRefused$ = this._deliveryRefused.asObservable();

  private _unavailableProduct = new Subject<UnavailableProductEvent>();
  unavailableProduct$ = this._unavailableProduct.asObservable();

  private productAvailabilityMap = new Map<number, { from: string; to: string }>();
  private productsUnavailableInMenu = new Set<number>();

  private currentOrderId: number | null = null;

  /**
   * The promo code to attach to the NEXT sync, and the reason it cannot live on the queued
   * operation's payload.
   *
   * ⚠️ `processQueue()` flattens the queue by reducing over `operation.payload.line_items`
   * and throws away everything else on each payload. A code carried there would be silently
   * dropped on every sync that coalesced more than one operation — which is most of them.
   *
   * Three states, matching the server contract exactly:
   *   `undefined` — nothing to say; the key is omitted and the order keeps what it has
   *   `''`        — the guest cleared the code; price at gross
   *   `'FEATHER20'` — apply this code
   */
  private pendingPromoCode: string | null | undefined = undefined;

  /**
   * The tip choice to attach to the NEXT sync — same reasoning, and same three states, as
   * pendingPromoCode: `undefined` omits the keys ("keep what the order has"), an empty value
   * means "no tip", a number is the choice.
   */
  private pendingTip: { tip_percent?: number | ''; tip_amount?: number | '' } | undefined = undefined;

  /**
   * The delivery location to attach to the NEXT sync. Same three states as the two above,
   * mapped onto the contract's `delivery_location`: `undefined` omits the key, `null` means
   * "not a delivery order" (removes the fee), an object is the guest's pin and address.
   */
  private pendingDeliveryLocation: DeliveryLocation | null | undefined = undefined;

  private syncQueue: SyncOperation[] = [];
  /**
   * The sync on the wire, or null. Doubles as processQueue()'s re-entrancy guard — see the
   * note there — and is what flushPendingSync() waits on. Resolves true on success.
   */
  private inFlightSync: Promise<boolean> | null = null;

  /**
   * True while the cart has edits the server has not priced yet: queued inside the debounce,
   * or on the wire. The total on screen is provisional for exactly that long — it is the
   * server's answer that re-checks a promo code — so the order bar dims it, and the order
   * button waits: ordering now would place the cart from before the edit. Mirrors
   * `isCartBusy` in the iOS OrderContainer.
   */
  private _syncing = new BehaviorSubject<boolean>(false);
  syncing$ = this._syncing.asObservable();
  private isOnline: boolean = navigator.onLine;
  private syncSubject = new Subject<void>();
  private maxRetries = 3;
  private readonly debounceTimeMs = 2000; // 2 seconds
  private readonly retryDelayMs = 2000;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private clientData: any = null;

  constructor(private apiService: ApiService,   private analytics: AnalyticsService) {
    // Initialize client data
    this.initializeClientData();

    // Initialize online/offline listeners
    window.addEventListener('online', () => this.handleOnlineStatus(true));
    window.addEventListener('offline', () => this.handleOnlineStatus(false));

    // Initialize debounced sync
    this.syncSubject.pipe(
      debounceTime(this.debounceTimeMs),
      filter(() => this.isOnline && this.clientData !== null)
    ).subscribe(() => {
      this.processQueue();
    });

    // Try to restore cart from localStorage
    this.restoreCart();
  }

  private initializeClientData() {
    try {
      const clientDataString = sessionStorage.getItem('clientData');
      if (clientDataString) {
        this.clientData = JSON.parse(clientDataString);
      }
      
      // Set up listener for clientData changes in sessionStorage
      window.addEventListener('storage', (e) => {
        if (e.key === 'clientData' && e.newValue) {
          this.clientData = JSON.parse(e.newValue);
        } 
      });
    } catch (error) {
      console.error('Error initializing client data:', error);
    }
  }

  private getClientData(): any {
    // Always get fresh client data from sessionStorage
    try {
      const clientDataString = sessionStorage.getItem('clientData');
      if (!clientDataString) {
        throw new Error('No client data found in session storage');
      }
      return JSON.parse(clientDataString);
    } catch (error) {
      console.error('Error getting client data:', error);
      return null;
    }
  }

  /**
   * Re-derive `syncing$` from the queue. Called after every change to `syncQueue`,
   * `inFlightSync` or the order id.
   *
   * A queue with no order to sync into does not count: processQueue() cannot send it, so
   * counting it would hold the order button forever.
   */
  private refreshSyncing(): void {
    const syncing = this.inFlightSync !== null || (this.syncQueue.length > 0 && this.currentOrderId !== null);
    if (this._syncing.getValue() !== syncing) {
      this._syncing.next(syncing);
    }
  }

  /** Whether the cart is ahead of the server right now — see `syncing$`. */
  get isSyncing(): boolean {
    return this._syncing.getValue();
  }

  private persistSyncQueue(): void {
    localStorage.setItem('syncQueue', JSON.stringify(this.syncQueue));
    this.refreshSyncing();
  }

  private handleOnlineStatus(online: boolean) {
    this.isOnline = online;
    if (online) {
      this.processQueue(); // Process pending operations when back online
    }
  }

  setCurrentOrderId(orderId: number) {
    this.currentOrderId = orderId;
    localStorage.setItem('currentOrderId', orderId.toString());
    this.refreshSyncing();
  }

  getCurrentOrderId(): number | null {
    return this.currentOrderId;
  }

  /**
   * Forget the order this cart was syncing into.
   *
   * ⚠️ Nulls the FIELD as well as the storage key, and that is the whole point.
   * The caller used to do `localStorage.removeItem('currentOrderId')` by hand, which
   * left `this.currentOrderId` pointing at the finished order. On a same-tab return
   * from BORICA — a Back press, or a decline handled in place — that stale id is still
   * live, so anything added to the cart would PUT line items straight into an order the
   * guest has already been charged for or is still being asked to pay for.
   *
   * Nothing may mint a new order id but MenuComponent's createInitialOrder(), so after
   * this the cart has no order to sync into until the guest goes back to the menu.
   */
  clearCurrentOrder(): void {
    this.currentOrderId = null;
    this.refreshSyncing();

    try {
      localStorage.removeItem('currentOrderId');
    } catch {
      /* best effort — a denied localStorage must not break checkout */
    }
  }

  /** Identity of a cart line: the product plus its exact add-on selection. */
  getLineKey(item: CartItem): string {
    return cartLineKey(item.id, item.supplements);
  }

  /**
   * Send the queued edits, or join the sync already on the wire.
   *
   * @returns true when the server has the queued cart (or there was nothing to send), false
   *          when the sync failed or could not start.
   */
  private processQueue(): Promise<boolean> {
    /*
     * ⚠️ One sync at a time.
     *
     * applyPromoCode() calls this directly to skip the 2 s debounce — the guest has just
     * pressed Apply and is watching the total. That leaves TWO triggers armed: the direct
     * call, and the debounced one queueSync() scheduled. If the PUT takes longer than the
     * debounce, the second fires while the first is still in flight, with the queue not yet
     * cleared, and sends the same lines and the same code a second time.
     *
     * Mirrors `isProcessingUpdates` in the iOS OrderContainer, which guards the same race.
     */
    if (this.inFlightSync) return this.inFlightSync;

    if (!this.syncQueue.length) return Promise.resolve(true);
    if (!this.isOnline || !this.currentOrderId) return Promise.resolve(false);

    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    // The guard is released before any follow-up starts, and on a throw as well, so a failed
    // sync cannot wedge it on and silently stop the cart syncing for the rest of the session.
    // A follow-up's answer becomes this sync's answer: flushPendingSync() must not report a
    // failure for a request refused only for its tip when the resend without it succeeds.
    this.inFlightSync = this.runSync()
      .catch((error): SyncOutcome => {
        console.error('Sync threw:', error);
        return { ok: false, next: 'none' };
      })
      .then(outcome => {
        this.inFlightSync = null;
        this.refreshSyncing();
        if (outcome.next === 'reconcile') return this.reconcileAfterDroppedEdits();
        if (outcome.next === 'now') return this.processQueue();
        return outcome.ok;
      });
    this.refreshSyncing();

    return this.inFlightSync;
  }

  private async runSync(): Promise<SyncOutcome> {
    // Read once: the whole sync belongs to the order it started on.
    const orderId = this.currentOrderId;
    if (orderId === null) return { ok: false, next: 'none' };

    const clientData = this.getClientData();
    if (!clientData || !clientData.id) {
      console.error('Invalid client data: missing shop ID');
      return { ok: false, next: 'none' };
    }

    const shopId = clientData.id;

    /*
     * ⚠️ Only the operations sent in THIS request are settled by its answer.
     *
     * Edits the guest makes while it is on the wire are pushed onto `syncQueue` too. Clearing
     * the whole queue on success used to throw those away unsent — the server never heard of
     * them, and the order went out without them.
     */
    const sentOperations = this.syncQueue.slice();
    const sentIds = new Set(sentOperations.map(operation => operation.id));
    const isReconcile = sentOperations.some(operation => operation.reconcile);
    // Captured for the same reason: a code or tip chosen while this request is in flight is
    // not the one it carried, and must survive the clears below.
    const sentPromoCode = this.pendingPromoCode;
    const sentTip = this.pendingTip;
    // And the location, so one confirmed (or a switch to pickup) mid-flight survives too.
    const sentDeliveryLocation = this.pendingDeliveryLocation;

    try {
      // Get current order to fetch existing line item IDs
      const currentOrder = await this.apiService.getOrder(shopId, orderId).toPromise();
      const existingLineItems: ServerLineItem[] = currentOrder.line_items || [];

      // Fix the type issue by explicitly typing the accumulator
      const allLineItems = sentOperations.reduce<LineItemPayload[]>((items, operation) => {
        return [...items, ...operation.payload.line_items];
      }, []);

      const uniqueLineItems = this.consolidateLineItems(allLineItems);

      // Map line items to include existing IDs
      const serverLinesByKey = this.indexServerLineItems(existingLineItems);
      const lineItemsWithIds: LineItemPayload[] = uniqueLineItems
        // Matched on the LINE key, never on product_id alone. With `.find(existing =>
        // existing.product_id === item.product_id)` the moment one product occupied two lines
        // both of them were handed the SAME server line id, and the second silently overwrote the
        // first on the next PUT — one of the guest's two variants just vanished.
        .map(item => ({ item, existingItem: serverLinesByKey.get(cartLineKey(item.product_id, item.supplements)) }))
        // A zero-quantity line the server does not have is a deletion of a row that was never
        // created — the guest added and removed inside one debounce window, or a re-key zeroed a
        // line the server had not seen yet. It must not be posted: WooCommerce reads a line item
        // with no `id` as an INSERT, so this would create the very row it is trying to delete, at
        // quantity zero, and every later sync would carry it.
        //
        // ⚠️ Decided on whether the server HAS the line, not on whether it has an `id`. Hype
        // orders carry no line ids at all — the backend matches their lines by product and
        // add-ons — so filtering on the id silently dropped every removal on a Hype venue and the
        // order kept items the guest had taken out of the cart.
        .filter(({ item, existingItem }) => item.quantity > 0 || existingItem != null)
        .map(({ item, existingItem }) => ({
          ...item,
          meta_data: this.withItemNoteClear(item.meta_data, existingItem),
          id: existingItem?.id ?? undefined // WooCommerce's row id; Hype lines have none
        }));

      if (isReconcile) {
        lineItemsWithIds.push(...this.serverOnlyLinesZeroed(existingLineItems, uniqueLineItems));
      }

      // `promo_code` rides the line-items request because that is the only request the
      // server prices from. It is added ONLY when there is something to say: an omitted key
      // means "keep whatever the order has", which is what an ordinary cart edit wants.
      const body: {
        line_items: typeof lineItemsWithIds;
        promo_code?: string;
        tip_percent?: number | '';
        tip_amount?: number | '';
        delivery_location?: DeliveryLocation | null;
      } = {
        line_items: lineItemsWithIds
      };
      if (sentPromoCode !== undefined) {
        body.promo_code = sentPromoCode ?? '';
      }
      // The tip rides the same request for the same reason: it is priced from these lines.
      if (sentTip !== undefined) {
        Object.assign(body, sentTip);
      }
      // And the location: the fee, the minimum and free delivery are all priced from these lines.
      if (sentDeliveryLocation !== undefined) {
        body.delivery_location = sentDeliveryLocation;
      }

      // ⚠️ The response is READ, not discarded. It carries `total`, `discount_total` and
      // `promo_code` — the authoritative figures. Before promo codes existed the local
      // reduce in updateTotal() was always right, so throwing this away cost nothing; with
      // a discount applied it is the only place the true total exists.
      const response = await this.apiService.updateOrder(
        shopId,
        orderId,
        body
      ).toPromise();

      // Cleared only on success. A failed sync leaves it pending so the retry still carries
      // it — otherwise a guest who applied a code while offline would lose it silently.
      if (this.pendingPromoCode === sentPromoCode) {
        this.pendingPromoCode = undefined;
      }
      if (this.pendingTip === sentTip) {
        this.pendingTip = undefined;
      }
      // Only the location this request carried. A newer one (or a switch to pickup) set while
      // it was in flight stays pending: applyDeliveryLocation() queued its own operation, which
      // survives the filter below and goes out next.
      const deliveryPriced = sentDeliveryLocation !== undefined
        && this.pendingDeliveryLocation === sentDeliveryLocation;
      if (deliveryPriced) {
        this.pendingDeliveryLocation = undefined;
      }

      this.applyServerOrderTotals(response);
      // After the figures are live, so the sheet never reads "priced" against the old quote.
      if (deliveryPriced) {
        this._deliveryPricing.next(false);
      }

      this.syncQueue = this.syncQueue.filter(operation => !sentIds.has(operation.id));
      this.persistSyncQueue();

      // Edits that arrived mid-flight go out straight away: the order button is waiting on them.
      return { ok: true, next: this.syncQueue.length ? 'now' : 'none' };
    } catch (error: any) {
      console.error('Sync failed:', error);

      /*
       * A refused tip refuses the WHOLE request, lines included. Drop the tip and send the
       * lines again straight away, rather than letting the retry loop resend the same
       * refused choice until it gives up and the cart silently stops syncing.
       */
      const tipError = this.tipRefusalCode(error);
      if (tipError) {
        if (this.pendingTip === sentTip) {
          this.pendingTip = undefined;
        }
        this._tipRefused.next(tipError);
      }

      // The same for a refused location (the venue stopped pricing delivery, or the pin was
      // malformed): drop it, tell the sheet, and re-send the lines without it.
      const deliveryError = this.deliveryRefusalCode(error);
      if (deliveryError && this.pendingDeliveryLocation === sentDeliveryLocation) {
        this.pendingDeliveryLocation = undefined;
      }
      if (sentDeliveryLocation !== undefined) {
        // Any other failure leaves the location pending, so the next sync still carries it;
        // the sheet only needs to stop waiting and let the guest try again.
        this._deliveryPricing.next(false);
        this._deliveryRefused.next(deliveryError ?? 'sync_failed');
      }

      if (error.status === 404) {
        console.error('Order not found:', {
          orderId,
          shopId: shopId
        });
      }

      /*
       * 400 and 404 are answers, not outages: the server refused these lines, or the order is
       * gone. Sending the same request again gets the same answer, so the operations are
       * dropped now rather than after three identical refusals. Anything else (a timeout, a
       * 5xx, the network dropping) is retried on a timer until the retries run out, rather than
       * leaving the cart unsynced — and the order bar waiting — until the guest taps again.
       */
      const orderGone = error?.status === 404;
      const isFinal = error?.status === 400 || orderGone;
      let droppedEdits = false;
      const survivors: SyncOperation[] = [];
      for (const operation of this.syncQueue) {
        // A request refused only for its tip is not a failed attempt at its lines: they go
        // straight back out without the tip and keep their full retry budget.
        if (sentIds.has(operation.id) && !tipError && !deliveryError) {
          operation.retryCount++;
          if (isFinal || operation.retryCount >= this.maxRetries) {
            droppedEdits = true;
            continue;
          }
        }
        survivors.push(operation);
      }
      this.syncQueue = survivors;
      this.persistSyncQueue();

      if (tipError || deliveryError) {
        return { ok: false, next: this.syncQueue.length ? 'now' : 'none' };
      }

      // A failed reconcile is not followed by another: that would loop on a cart the server
      // keeps refusing. The guest's next edit sends the whole cart again anyway. Nor is there
      // anything to reconcile into when the order is gone, or the cart has moved to another.
      if (droppedEdits && !isReconcile && !orderGone && orderId === this.currentOrderId) {
        return { ok: false, next: 'reconcile' };
      }

      if (!isFinal && this.syncQueue.length) {
        this.retryTimer = setTimeout(() => {
          this.retryTimer = null;
          this.processQueue();
        }, this.retryDelayMs);
      }

      return { ok: false, next: 'none' };
    }
  }

  /**
   * Get every cart edit to the server and wait for its answer — what Order waits on.
   *
   * ⚠️ Without this the order could be placed against a cart the server had not seen. Edits
   * sit in a 2 s debounce, and the web submit carries no line items: the server re-checked
   * the promo code against the STORED cart, placed the order at that price, and the queued
   * edit then landed on an order that had already been submitted. A code whose minimum the
   * edit had broken went through discounted.
   *
   * Skips the debounce, waits out a sync already on the wire, then sends whatever is left.
   * A code the server refuses on the way surfaces through `promoCodeRefused$` before this
   * resolves, so the caller can stop and let the guest see the new total.
   *
   * @returns true when the server has priced the cart on screen; false when a sync failed
   *          or the guest is offline, and the order must not go ahead.
   */
  async flushPendingSync(): Promise<boolean> {
    // Bounded: each pass either empties the queue or fails, but a guest can keep tapping.
    for (let pass = 0; pass < 5; pass++) {
      if (this.inFlightSync) {
        if (!(await this.inFlightSync)) return false;
        continue;
      }

      if (!this.syncQueue.length) return true;

      if (!(await this.processQueue())) return false;
    }

    return !this.syncQueue.length && !this.inFlightSync;
  }

  /** The server's delivery error code (`delivery_*`, HTTP 422), or null for any other failure. */
  private deliveryRefusalCode(error: any): string | null {
    const code = error?.status === 422 ? error?.error?.code : null;
    return typeof code === 'string' && code.startsWith('delivery_') ? code : null;
  }

  /** The server's tip error code (`tip_*` / `tips_*`, HTTP 422), or null for any other failure. */
  private tipRefusalCode(error: any): string | null {
    const code = error?.status === 422 ? error?.error?.code : null;
    return typeof code === 'string' && /^tips?_/.test(code) ? code : null;
  }

  /*
   * Edits were dropped, so the server's order may still hold lines the guest has since removed:
   * every edit re-sends the whole cart, but a removal is only ever said once, as a zero line.
   * Queue one more full-cart sync that also zeroes every server row the cart no longer has.
   */
  private reconcileAfterDroppedEdits(): Promise<boolean> {
    if (!this.currentOrderId) return Promise.resolve(false);
    this.syncQueue.push({
      id: Math.random().toString(36).substr(2, 9),
      payload: { line_items: this.formatLineItems(this.cartItems.getValue(), 'update') },
      timestamp: Date.now(),
      retryCount: 0,
      reconcile: true
    });
    this.persistSyncQueue();
    return this.processQueue();
  }

  /** Zero-quantity deletions for the server rows that no line in `cartLines` matches. */
  private serverOnlyLinesZeroed(serverLines: ServerLineItem[], cartLines: LineItemPayload[]): LineItemPayload[] {
    const cartKeys = new Set(cartLines.map(line => cartLineKey(line.product_id, line.supplements)));
    const zeroed: LineItemPayload[] = [];
    for (const line of serverLines) {
      // A Hype line has no `id` (the backend keys it by product and add-ons), so a missing id
      // must not exempt it: zero it by product all the same.
      if (line?.product_id == null) continue;
      const supplements = normalizeCartSupplements(line.supplements);
      if (cartKeys.has(cartLineKey(line.product_id, supplements))) continue;
      const payload: LineItemPayload = {
        id: line.id ?? undefined,
        product_id: line.product_id,
        quantity: 0,
        subtotal: '0',
        total: '0',
        meta_data: []
      };
      if (supplements) {
        payload.supplements = supplements.map(supplement => supplementPayload(supplement));
      }
      zeroed.push(payload);
    }
    return zeroed;
  }

  /**
   * The server's line items, keyed the way this client keys its own.
   *
   * Their `supplements` are normalized first: the server may spell a price as a string, and a key
   * built from the raw rows would then never match the one built from the local cart, so every
   * line would look new and be re-inserted.
   *
   * Two rows under one key should not happen — the backend merges by this same key — but if it
   * ever does, the lowest line id wins rather than "whichever the iterator saw last". iOS built
   * this map with a uniquing dictionary initialiser, which TRAPS on a duplicate key: a literal
   * crash waiting for the first order the backend returned unmerged. Picking the oldest row keeps
   * repeated PUTs converging on the same one instead of ping-ponging between two.
   */
  private indexServerLineItems(lineItems: ServerLineItem[]): Map<string, ServerLineItem> {
    const byKey = new Map<string, ServerLineItem>();
    for (const item of lineItems) {
      if (item?.product_id == null) continue;
      const key = cartLineKey(item.product_id, normalizeCartSupplements(item.supplements));
      const incumbent = byKey.get(key);
      if (!incumbent || (item.id ?? Number.MAX_SAFE_INTEGER) < (incumbent.id ?? Number.MAX_SAFE_INTEGER)) {
        byKey.set(key, item);
      }
    }
    return byKey;
  }

  /**
   * Add an explicit `item_note: ""` when the guest has cleared a note the SERVER still holds.
   *
   * `formatLineItems` omits the entry on a line without a note, which is right for every line that
   * never had one — but WooCommerce updates line meta BY KEY, so an omitted key leaves the stored
   * note exactly where it was and the kitchen keeps reading "no onion" after the guest removed it.
   * The server's own copy of the line is already in hand here, so this is decided from what the
   * server actually has rather than from local bookkeeping that a restored cart could have lost.
   */
  private withItemNoteClear(
    metaData: LineItemPayload['meta_data'],
    serverLine: ServerLineItem | undefined
  ): LineItemPayload['meta_data'] {
    if (!serverLine || metaData.some(meta => meta.key === ITEM_NOTE_META_KEY)) return metaData;
    if (itemNoteFromMeta(serverLine.meta_data) === '') return metaData;
    return [...metaData, { key: ITEM_NOTE_META_KEY, value: '' }];
  }

  private consolidateLineItems(lineItems: LineItemPayload[]): LineItemPayload[] {
    const itemMap = new Map<string, LineItemPayload>();

    // Process items in reverse to keep the latest update
    lineItems.reverse().forEach(item => {
      // Keyed by LINE, not by product: two add-on variants of one dish are two rows in the same
      // payload, and collapsing them here would post only whichever the queue saw last.
      const key = cartLineKey(item.product_id, item.supplements);
      if (!itemMap.has(key)) {
        itemMap.set(key, item);
      }
    });

    return Array.from(itemMap.values());
  }

  /**
   * Apply a promo code, clear it, and re-sync so the server re-prices the cart.
   *
   * Pass a code to apply it; pass `''` to clear it. Either way the full line-items payload
   * is re-sent, because the discount is rendered into LINE prices server-side and there is
   * no other request that carries them.
   *
   * ⚠️ Bypasses the 2 s debounce on purpose. That delay exists to coalesce quantity taps,
   * where nothing visible waits on the result. Here the guest has just pressed Apply and is
   * watching for the total to move; two seconds of nothing reads as a dead button.
   */
  applyPromoCode(code: string | null): void {
    if (!this.currentOrderId) {
      // queueSync() would bail silently. Callers gate the UI on hasOrder(), so reaching
      // here means the order went away underneath us.
      console.error('Cannot apply a promo code before an order exists');
      return;
    }

    this.pendingPromoCode = code ?? '';

    this.queueSync({
      line_items: this.formatLineItems(this.cartItems.getValue(), 'update')
    });

    // Straight past the debounce — see above.
    this.processQueue();
  }

  /**
   * Choose a tip and re-sync so the server prices it. Bypasses the debounce for the same
   * reason as applyPromoCode(): the guest has just tapped and is watching the total.
   */
  applyTip(choice: TipChoice): void {
    if (!this.currentOrderId) {
      console.error('Cannot set a tip before an order exists');
      return;
    }

    this.pendingTip = choice.kind === 'percent'
      ? { tip_percent: choice.percent }
      : choice.kind === 'fixed'
        ? { tip_amount: choice.amount }
        : { tip_percent: '', tip_amount: '' };

    this.queueSync({
      line_items: this.formatLineItems(this.cartItems.getValue(), 'update')
    });

    this.processQueue();
  }

  /**
   * Set where the order goes — a pin and an address, or null for "not a delivery order" —
   * and re-sync so the server prices it. A normal cart update, never the submit: the guest
   * sees the fee and the new total before they can place the order.
   */
  applyDeliveryLocation(location: DeliveryLocation | null): void {
    if (!this.currentOrderId) {
      console.error('Cannot set a delivery location before an order exists');
      return;
    }

    this.pendingDeliveryLocation = location;
    this._deliveryPricing.next(true);

    this.queueSync({
      line_items: this.formatLineItems(this.cartItems.getValue(), 'update')
    });

    this.processQueue();
  }

  /**
   * Where the current order is going, read from the server — for the Order-now sheet after
   * a reload, when the cart has kept the fee but not the pin. Null for none (or on failure:
   * the sheet then simply looks the address up again).
   *
   * Adopts the delivery figures it finds, and only those. The totals stay the sync's to
   * adopt, so this can never race a cart edit into showing an older total.
   */
  async fetchDeliveryLocation(): Promise<DeliveryLocation | null> {
    const shopId = this.getClientData()?.id;
    if (!this.currentOrderId || !shopId) return null;

    try {
      const order = await this.apiService.getOrder(shopId, this.currentOrderId).toPromise();
      // A location sent while this was in flight is newer than what the GET saw.
      if (!this._deliveryPricing.getValue()) {
        this._deliveryFee.next(parseOrderDeliveryFee(order));
        this._deliveryQuote.next(parseDeliveryQuote(order));
      }
      return parseOrderDeliveryLocation(order);
    } catch {
      return null;
    }
  }

  /**
   * Adopt the order a refused SUBMIT came back with (HTTP 422 `delivery_*`), so the sheet and
   * the cart show the figures the guest is being asked about. Nothing is placed.
   */
  adoptRefusedOrder(order: any): void {
    this.applyServerOrderTotals(order);
  }

  /** Whether there is a server order to attach a code to. The promo UI is gated on this. */
  hasOrder(): boolean {
    return this.currentOrderId !== null;
  }

  /**
   * Adopt the server's own figures for an order.
   *
   * ⚠️ These arrive as STRINGS (`"112.00"`), unlike the numbers the validate endpoint
   * returns. Parsed once here, at the boundary, so `_total` stays a `number` — several
   * callers do arithmetic on it, including `getSecondPrice()`.
   *
   * A missing or unparseable `total` leaves the local optimistic value alone rather than
   * blanking the cart: an older backend, or one with the feature switched off, sends
   * neither field and must keep working exactly as before.
   */
  private applyServerOrderTotals(response: any): void {
    if (!response) return;

    const serverTotal = parseFloat(response.total);
    if (!isNaN(serverTotal)) {
      this._total.next(serverTotal);
    }

    const discount = parseFloat(response.discount_total);
    this._discountTotal.next(isNaN(discount) ? 0 : discount);

    const applied = typeof response.promo_code === 'string' && response.promo_code !== ''
      ? response.promo_code
      : null;
    this._appliedPromoCode.next(applied);

    this._tip.next(parseOrderTip(response));

    this._deliveryFee.next(parseOrderDeliveryFee(response));
    this._deliveryQuote.next(parseDeliveryQuote(response));

    this.saveCartToStorage();

    // Last, and only after the figures above are live: a listener reacting to this will read
    // the corrected total, which is the entire point of telling it.
    if (typeof response.promo_code_refused === 'string' && response.promo_code_refused !== '') {
      const minimum = parseFloat(response.minimum_order_amount);
      this._promoCodeRefused.next({
        reason: response.promo_code_refused,
        minimumOrderAmount: isNaN(minimum) ? undefined : minimum,
      });
    }
  }

  private queueSync(payload: any) {
    if (!this.currentOrderId) {
      console.error('No current order ID available');
      return;
    }

    const clientData = this.getClientData();
    if (!clientData || !clientData.id) {
      console.error('Invalid client data: missing shop ID, sync operation cancelled');
      return;
    }

    // Add operation to queue
    const operation: SyncOperation = {
      id: Math.random().toString(36).substr(2, 9),
      payload,
      timestamp: Date.now(),
      retryCount: 0
    };

    this.syncQueue.push(operation);
    this.persistSyncQueue();

    // Debounce the sync to collect multiple operations
    this.syncSubject.next();
  }

  private saveCartToStorage() {
    localStorage.setItem('cartItems', JSON.stringify(this.cartItems.getValue()));
    localStorage.setItem('cartTotal', this._total.getValue().toString());
    // Stored alongside the total because they have to agree. `cartTotal` is already the NET
    // figure once a code is applied, so restoring it without the code would show a
    // discounted total with no explanation of where the discount came from.
    localStorage.setItem('cartDiscountTotal', this._discountTotal.getValue().toString());
    const applied = this._appliedPromoCode.getValue();
    if (applied) {
      localStorage.setItem('cartPromoCode', applied);
    } else {
      localStorage.removeItem('cartPromoCode');
    }
    // With the total for the same reason as the discount: `cartTotal` includes it.
    const tip = this._tip.getValue();
    if (tip) {
      localStorage.setItem('cartTip', JSON.stringify(tip));
    } else {
      localStorage.removeItem('cartTip');
    }
    // `cartTotal` includes the fee too. Only the fee: the quote is re-sent with every sync.
    const deliveryFee = this._deliveryFee.getValue();
    if (deliveryFee !== null) {
      localStorage.setItem('cartDeliveryFee', deliveryFee.toString());
    } else {
      localStorage.removeItem('cartDeliveryFee');
    }
  }

  private restoreCart() {
    const savedCart = localStorage.getItem('cartItems');
    const savedTotal = localStorage.getItem('cartTotal');
    const savedOrderId = localStorage.getItem('currentOrderId');
    const savedQueue = localStorage.getItem('syncQueue');

    if (savedCart) {
      const restored: CartItem[] = JSON.parse(savedCart);
      // Carts saved before add-ons existed simply have no `supplements` and pass through
      // untouched. The ones that do carry them are re-coerced rather than trusted: a single line
      // whose price came back as a string would make `cartLineTotal` return NaN, and one NaN in
      // the reduce blanks the total for the WHOLE cart, not just that line.
      this.cartItems.next(
        Array.isArray(restored)
          ? restored.map(item =>
              item.supplements
                ? { ...item, supplements: normalizeCartSupplements(item.supplements) }
                : item
            )
          : []
      );
    }
    if (savedTotal) {
      this._total.next(parseFloat(savedTotal));
    }

    // Restored for display continuity only. The server re-validates the code on the next
    // sync, so a code that expired while the tab was closed is corrected then — the guest
    // sees the stale figure for as long as it takes one PUT to answer, not until submit.
    try {
      const savedTip = JSON.parse(localStorage.getItem('cartTip') || 'null');
      if (savedTip && typeof savedTip.amount === 'number') {
        this._tip.next(savedTip);
      }
    } catch {
      /* a corrupt entry is just no tip shown until the next sync */
    }
    const savedDeliveryFee = parseFloat(localStorage.getItem('cartDeliveryFee') ?? '');
    if (!isNaN(savedDeliveryFee)) {
      this._deliveryFee.next(savedDeliveryFee);
    }

    const savedDiscount = localStorage.getItem('cartDiscountTotal');
    if (savedDiscount) {
      const parsed = parseFloat(savedDiscount);
      this._discountTotal.next(isNaN(parsed) ? 0 : parsed);
    }
    const savedPromoCode = localStorage.getItem('cartPromoCode');
    if (savedPromoCode) {
      this._appliedPromoCode.next(savedPromoCode);
    }
    if (savedOrderId) {
      this.currentOrderId = parseInt(savedOrderId);
    }
    if (savedQueue) {
      this.syncQueue = JSON.parse(savedQueue);
      this.refreshSyncing();
      if (this.isOnline && this.syncQueue.length) {
        this.processQueue();
      }
    }
  }

  private getProductParentCategoryName(product: CartItem): string {
    if (!product.categories || product.categories.length === 0) {
      return '';
    }
    const categories = product.categories as any[];
    const mainCategoryId = Math.min(...categories.map(c => c.id ?? 0));
    const mainCategory = categories.find(c => (c.id ?? 0) === mainCategoryId);
    return mainCategory?.name ?? '';
  }

  private formatLineItems(items: CartItem[], action: 'add' | 'update' | 'delete'): LineItemPayload[] {
    return items.map(item => {
      const meta_data = [
        { key: 'regular_price', value: String(item.regular_price) },
        { key: 'sku', value: String(item.sku) },
        { key: 'category_name', value: this.getProductParentCategoryName(item) }
      ];

      // Rebuilt from the line on EVERY sync, quantity taps included — this is the one place line
      // meta is assembled, so a note left out here would be wiped by the next stepper press.
      // Clearing a note the server already holds is handled in processQueue, which can see it.
      const note = normalizeItemNote(item.note);
      if (note) {
        meta_data.push({ key: ITEM_NOTE_META_KEY, value: note });
      }

      // Calculate the total price for this item, add-ons included. The server re-prices from its
      // own catalogue anyway, but posting the base-only figure would make the guest's screen
      // disagree with the order until the response comes back.
      const itemTotal = cartLineTotal(item.price, item.quantity, item.supplements);
      const isDelete = action === 'delete';

      const lineItem: LineItemPayload = isDelete
        ? {
            product_id: item.id,
            quantity: 0,
            subtotal: "0",
            total: "0",
            meta_data
          }
        : {
            product_id: item.id,
            quantity: item.quantity,
            subtotal: String(itemTotal),
            total: String(itemTotal),
            meta_data
          };

      // Attached only when the line actually has add-ons — an empty array would change the bytes
      // every WooCommerce venue posts today. Sent on the delete path too: it is part of the line's
      // identity, so without it the server cannot tell WHICH of the product's rows to zero.
      if (item.supplements && item.supplements.length > 0) {
        lineItem.supplements = item.supplements.map(supplement => supplementPayload(supplement));
      }

      return lineItem;
    });
  }

  buildAvailabilityMap(categories: CategoryWithProducts[]): void {
    this.productAvailabilityMap.clear();
    for (const category of categories) {
      if (category.available_from && category.available_to &&
          category.available_from.trim() && category.available_to.trim()) {
        const window = { from: category.available_from, to: category.available_to };
        for (const product of category.products ?? []) {
          if (product.id) this.productAvailabilityMap.set(product.id, window);
        }
        for (const sub of category.subcategories ?? []) {
          for (const product of sub.products ?? []) {
            if (product.id) this.productAvailabilityMap.set(product.id, window);
          }
        }
      }
    }
  }

  buildUnavailableInMenuSet(featuredProducts: ProductResponse[], categories: CategoryWithProducts[]): void {
    this.productsUnavailableInMenu.clear();
    for (const product of featuredProducts) {
      if (product.id && product.available_in_the_menu === false) {
        this.productsUnavailableInMenu.add(product.id);
      }
    }
    for (const category of categories) {
      for (const product of category.products ?? []) {
        if (product.id && product.available_in_the_menu === false) {
          this.productsUnavailableInMenu.add(product.id);
        }
      }
      for (const sub of category.subcategories ?? []) {
        for (const product of sub.products ?? []) {
          if (product.id && product.available_in_the_menu === false) {
            this.productsUnavailableInMenu.add(product.id);
          }
        }
      }
    }
  }

  isProductCurrentlyAvailable(productId: number): boolean {
    if (this.productsUnavailableInMenu.has(productId)) return false;
    const window = this.productAvailabilityMap.get(productId);
    if (!window) return true;
    const now = new Date();
    const nowStr = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');
    return nowStr >= window.from && nowStr <= window.to;
  }

  getAvailabilityWindow(productId: number): { from: string; to: string } | undefined {
    return this.productAvailabilityMap.get(productId);
  }

  /**
   * Products in the cart that cannot be ordered right now.
   *
   * Availability is a property of the PRODUCT, not of an add-on selection, so this stays
   * product-keyed — but a dish held under two selections is two lines and would otherwise be
   * listed twice in the same "these are unavailable" sentence.
   */
  getUnavailableCartItems(): { name: string; productId: number }[] {
    const items = this.cartItems.getValue();
    const unavailable: { name: string; productId: number }[] = [];
    const reported = new Set<number>();
    for (const item of items) {
      if (reported.has(item.id)) continue;
      reported.add(item.id);
      if (!this.isProductCurrentlyAvailable(item.id)) {
        unavailable.push({ name: item.name, productId: item.id });
      }
    }
    return unavailable;
  }

  private checkAvailabilityGuard(productId: number, productName: string): boolean {
    if (this.isProductCurrentlyAvailable(productId)) return true;
    const isUnavailableInMenu = this.productsUnavailableInMenu.has(productId);
    const window = isUnavailableInMenu ? undefined : this.productAvailabilityMap.get(productId);
    this._unavailableProduct.next({
      productId,
      productName,
      from: window?.from,
      to: window?.to
    });
    return false;
  }

  /**
   * Add to the line matching this exact add-on selection, creating it if there is none.
   *
   * The existing two-argument calls keep working and keep meaning what they meant: with no
   * `supplements` the key is `"<productId>|"`, the product's one and only line at a venue without
   * add-ons.
   */
  addToCart(product: any, source: string, supplements?: CartSupplement[], quantity: number = 1) {
    if (!this.checkAvailabilityGuard(product.id, product.name)) return;
    const selection = normalizeCartSupplements(supplements);
    const lineKey = cartLineKey(product.id, selection);
    const added = Math.max(1, Math.trunc(quantity));

    const currentItems = this.cartItems.getValue();
    const existingItem = currentItems.find(item => this.getLineKey(item) === lineKey);

    let updatedItems: CartItem[];
    if (existingItem) {
      // Update existing item
      updatedItems = currentItems.map(item =>
        this.getLineKey(item) === lineKey
          ? { ...item, quantity: item.quantity + added }
          : item
      );

      this.queueSync({
        line_items: this.formatLineItems(updatedItems, 'update')
      });
    } else {
      // Add new item
      const newItem: CartItem = {
        id: product.id,
        name: product.name,
        price: parseFloat(product.on_sale ? product.sale_price : product.regular_price),
        quantity: added,
        image: product.images[0]?.src,
        meta_data: product.meta_data,
        sku: product.sku,
        regular_price: product.regular_price,
        categories: product.categories,
        supplements: selection
      };
      updatedItems = [...currentItems, newItem];

      this.queueSync({
        line_items: this.formatLineItems(updatedItems, 'update')
      });
    }

    this.cartItems.next(updatedItems);

    this.analytics.log('product_added', {
      product_id: product.id.toString(),
      product_name: product.name,
      quantity: existingItem ? existingItem.quantity + added : added,
      add_source: this.mapAddSource(source)
    });

    this.updateTotal();
    this.saveCartToStorage();
  }

  /** Drop one specific line — the product under this exact add-on selection. */
  removeLine(lineKey: string, source: string) {
    const currentItems = this.cartItems.getValue();
    const itemToRemove = currentItems.find(item => this.getLineKey(item) === lineKey);
    if (!itemToRemove) return;

    // Send the full cart with only this line set to quantity 0, so the server deletes its row;
    // only then is it dropped locally.
    const itemsWithZeroQuantity = currentItems.map(item =>
      this.getLineKey(item) === lineKey ? { ...item, quantity: 0 } : item
    );

    this.queueSync({
      line_items: this.formatLineItems(itemsWithZeroQuantity, 'update')
    });

    const updatedItems = currentItems.filter(item => this.getLineKey(item) !== lineKey);
    this.cartItems.next(updatedItems);

    this.analytics.log('product_removed', {
      product_id: itemToRemove.id.toString(),
      product_name: itemToRemove.name,
      remove_source: this.mapRemoveSource(source)
    });

    this.updateTotal();
    this.saveCartToStorage();
  }

  /**
   * Remove EVERY line of a product, whatever the add-ons — "this dish is off the menu, take it out".
   *
   * Spelled out as its own method rather than hidden inside `removeFromCart`, because destroying
   * several lines from one product id is exactly what a row- or tile-scoped caller must never do by
   * accident: those hold one line and mean one line. Only a caller that genuinely means the whole
   * dish should be able to reach this, and now it has to say so.
   *
   * `removeUnavailableItemsAndSubmit` is that caller. It removes by product id and then re-runs the
   * submit, so a call that quietly removed nothing left the same "these items are unavailable" modal
   * reappearing forever with no way past it — and a call that removed only one of the dish's lines
   * would leave the others to fail the same check on the next pass.
   *
   * All the lines are zeroed in ONE payload rather than looped through `removeLine`, which would
   * queue a sync and log a `product_removed` per line; the pre-change code logged one event per
   * call and that shape is preserved.
   */
  removeProduct(productId: number, source: string) {
    const currentItems = this.cartItems.getValue();
    const doomed = currentItems.filter(item => item.id === productId);
    if (doomed.length === 0) return;

    // Full cart with every line of this product at zero, so the server deletes each row; only then
    // are they dropped locally.
    const itemsWithZeroQuantity = currentItems.map(item =>
      item.id === productId ? { ...item, quantity: 0 } : item
    );

    this.queueSync({
      line_items: this.formatLineItems(itemsWithZeroQuantity, 'update')
    });

    this.cartItems.next(currentItems.filter(item => item.id !== productId));

    this.analytics.log('product_removed', {
      product_id: productId.toString(),
      product_name: doomed[0].name,
      remove_source: this.mapRemoveSource(source)
    });

    this.updateTotal();
    this.saveCartToStorage();
  }

  /**
   * Remove a product's single line.
   *
   * The pre-add-ons signature, still the entry point for the tile fast paths that handle a dish
   * without add-ons — which can only ever occupy one line, so for them this is exactly what it
   * always was.
   *
   * When the product occupies SEVERAL lines this does nothing, and that inertness is the point. The
   * callers left on this signature are row- and tile-scoped: they were reached by a guest pointing
   * at one row, and they pass a product id only because that is all the old API took. Deleting every
   * variant on their behalf is the worst available reading — a guest taking their one plain pizza to
   * zero would silently lose the three cheese pizzas on the row above, with no undo. That is a
   * strictly worse failure than a button that does nothing, and it is invisible until the order
   * reaches the pass.
   *
   * The multi-line case is only reachable at all when a tile's own routing has already misjudged the
   * dish — a venue that switched `is_supplements_supported` off underneath a stocked cart, or a
   * payload that served this product without its `supplements`. This is the backstop for that.
   * A caller that knows which line it means uses `removeLine`; one that means the whole dish uses
   * `removeProduct`.
   */
  removeFromCart(productId: number, source: string) {
    const lineKey = this.resolveSingleLineKey(productId);
    if (!lineKey) return;
    this.removeLine(lineKey, source);
  }

  /** Set the quantity of one specific line. Dropping to zero removes it. */
  updateLineQuantity(lineKey: string, quantity: number, changeSource: string = 'menu') {
    const currentItems = this.cartItems.getValue();
    const existingItem = currentItems.find(item => this.getLineKey(item) === lineKey);
    if (!existingItem) return;

    // A line at zero is a line that is gone: keeping it locally would leave the cart showing a row
    // no surface can act on, and every current caller already guards this by calling remove
    // instead. It is routed rather than rejected so the two spellings cannot drift apart.
    if (quantity <= 0) {
      this.removeLine(lineKey, changeSource);
      return;
    }

    if (quantity > existingItem.quantity) {
      if (!this.checkAvailabilityGuard(existingItem.id, existingItem.name)) return;
    }

    const oldQuantity = existingItem.quantity;
    const updatedItems = currentItems.map(item =>
      this.getLineKey(item) === lineKey ? { ...item, quantity: quantity } : item
    );

    // Send the full updated cart to the server
    this.queueSync({
      line_items: this.formatLineItems(updatedItems, 'update')
    });

    this.cartItems.next(updatedItems);

    this.analytics.log('product_quantity_changed', {
      product_id: existingItem.id.toString(),
      product_name: existingItem.name,
      old_quantity: oldQuantity,
      new_quantity: quantity,
      change_source: changeSource
    });

    this.updateTotal();
    this.saveCartToStorage();
  }

  /**
   * Set the quantity of a product.
   *
   * The pre-add-ons signature, kept working for the tiles and sheets that have not been taught
   * about lines yet. It acts on the product's only line and is inert when the product occupies
   * several — see `resolveSingleLineKey` for why an absolute quantity must not be retargeted.
   * (`removeFromCart` deliberately does NOT share that resolution: removal is unambiguous.)
   */
  updateQuantity(productId: number, quantity: number, changeSource: string = 'menu') {
    const lineKey = this.resolveSingleLineKey(productId);
    if (!lineKey) return;
    this.updateLineQuantity(lineKey, quantity, changeSource);
  }

  /**
   * Move an existing line onto a DIFFERENT add-on selection, in place.
   *
   * A line's identity IS its selection, so "change this line's add-ons" is not an update of one
   * row: the old key goes to zero and the new key takes the quantity. Both halves ride in the SAME
   * payload — `{id, quantity: 0}` deletes the old row, `{product_id, quantity, supplements}`
   * inserts the new one — which is the same pair this service already posts when a guest zeroes
   * one product and adds another inside one debounce window, so the payload shape is unchanged.
   *
   * Mutating `line.supplements` in place instead would keep the row's server id while its key
   * changed underneath it, and `processQueue` would then hand that id to the wrong row.
   *
   * If the new key is ALREADY in the cart the two merge, quantities added. A guest editing "Pizza
   * + cheese" into "Pizza + bacon" while already holding a bacon pizza ends with one bacon line —
   * which is what every other surface would show them anyway.
   *
   * Deliberately NOT routed through `addToCart` / `removeFromCart`: those log `product_added` /
   * `product_removed`, and an edit is neither. Attributing it as both would double-count the dish
   * in the funnel.
   *
   * @param quantity The quantity the line should end up with. Defaults to whatever the line
   *   currently holds, so a pure add-on change keeps its count.
   */
  changeSupplements(
    line: CartItem,
    newSupplements?: CartSupplement[],
    quantity?: number,
    source: string = 'product_details'
  ) {
    const selection = normalizeCartSupplements(newSupplements);
    const oldKey = this.getLineKey(line);
    const newKey = cartLineKey(line.id, selection);

    const currentItems = this.cartItems.getValue();
    // The LIVE line, not the caller's snapshot: the sheet may have been open while a stepper
    // elsewhere moved the quantity.
    const liveLine = currentItems.find(item => this.getLineKey(item) === oldKey);
    const liveQuantity = liveLine?.quantity ?? 0;
    const wanted = Math.max(quantity ?? liveQuantity, 0);

    // Same selection — this is only a quantity change, and the single-line path already does
    // exactly that. No `supplements_changed` here either: nothing about the selection changed.
    if (oldKey === newKey) {
      if (wanted !== liveQuantity) {
        this.updateLineQuantity(oldKey, wanted, source);
      }
      return;
    }

    // The availability gate exists to stop a guest ORDERING something that is off-menu right now,
    // and this is the third mutation entry point — `addToCart` and `updateLineQuantity` both guard,
    // so without this one an edit is a way around them: ticking an add-on and raising the stepper
    // in the same save would grow a line on a dish the service itself reports as unavailable.
    //
    // Only an edit that ASKS FOR MORE is gated, matching the sheet's own rule on iOS: a higher
    // quantity, or an add-on that was not on the line before. Taking something away must never be
    // blocked — a guest who cannot reduce their order is stuck with it.
    const previousIds = new Set((line.supplements ?? []).map(supplement => supplement.id));
    const addsSupplement = (selection ?? []).some(supplement => !previousIds.has(supplement.id));
    if ((wanted > liveQuantity || addsSupplement) &&
        !this.checkAvailabilityGuard(line.id, line.name)) {
      return;
    }

    // Zero the old key FIRST: when the guest is moving onto a key that already exists, the other
    // order lets the local array double-count for a render pass.
    const zeroedOld = currentItems.map(item =>
      this.getLineKey(item) === oldKey ? { ...item, quantity: 0 } : item
    );

    const targetExists = zeroedOld.some(item => this.getLineKey(item) === newKey);
    // The note follows the guest's line to its new key — it is about the dish they are holding, not
    // about the add-on combination it used to have. Landing on an existing line, the two notes are
    // merged with each prefixed by the quantity it was written about (see `mergeItemNotes`).
    const movedNote = (liveLine ?? line).note;
    let payloadItems: CartItem[];
    if (targetExists) {
      payloadItems = zeroedOld.map(item => {
        if (this.getLineKey(item) !== newKey) return item;
        const { note: _previous, ...rest } = item;
        const merged = mergeItemNotes(
          { note: item.note, quantity: item.quantity },
          { note: movedNote, quantity: wanted }
        );
        return merged
          ? { ...rest, quantity: item.quantity + wanted, note: merged }
          : { ...rest, quantity: item.quantity + wanted };
      });
    } else if (wanted > 0) {
      // Everything but the selection is a property of the product, so the line being edited is the
      // donor for name / price / image / category — and for the note.
      const donor = liveLine ?? line;
      payloadItems = [...zeroedOld, { ...donor, quantity: wanted, supplements: selection }];
    } else {
      payloadItems = zeroedOld;
    }

    this.queueSync({
      line_items: this.formatLineItems(payloadItems, 'update')
    });

    // The zeroed old line has already been handed to the payload above; it must not survive
    // locally, or the cart would render a row at zero.
    this.cartItems.next(payloadItems.filter(item => item.quantity > 0));

    // Same event name AND same parameter shape as iOS (`AnalyticsEvents.supplementsChanged`): the
    // before/after counts are packed into one `type` string rather than sent as two numbers, so a
    // dashboard query can span both platforms. The add-on ids themselves are deliberately not sent
    // — the dimension worth reporting is whether guests are adding to a plain dish or trimming a
    // loaded one, not which topping.
    this.analytics.log('supplements_changed', {
      product_id: line.id.toString(),
      type: `${(line.supplements ?? []).length}->${(selection ?? []).length}`
    });

    this.updateTotal();
    this.saveCartToStorage();
  }

  /**
   * Set (or, with an empty string, remove) the note on one line.
   *
   * Syncs straight past the debounce, like `applyPromoCode`: the guest has just pressed Save and
   * may press Order next, and the submit carries no line items of its own — a note still sitting in
   * a 2 s debounce window when the order is placed would never reach the kitchen.
   *
   * Works with no server order yet as well: the note is kept locally and goes out with the line's
   * first sync.
   */
  setLineNote(lineKey: string, note: string) {
    const currentItems = this.cartItems.getValue();
    const existingItem = currentItems.find(item => this.getLineKey(item) === lineKey);
    if (!existingItem) return;

    const normalized = normalizeItemNote(note);
    const previous = normalizeItemNote(existingItem.note);
    if (normalized === previous) return;

    const updatedItems = currentItems.map(item => {
      if (this.getLineKey(item) !== lineKey) return item;
      const { note: _dropped, ...rest } = item;
      return normalized ? { ...rest, note: normalized } : rest;
    });

    this.cartItems.next(updatedItems);
    this.saveCartToStorage();

    this.analytics.log(
      !normalized ? 'item_note_removed' : previous ? 'item_note_changed' : 'item_note_added',
      { product_id: existingItem.id.toString() }
    );

    if (!this.currentOrderId) return;
    this.queueSync({
      line_items: this.formatLineItems(updatedItems, 'update')
    });
    this.processQueue();
  }

  /**
   * Quantity of a product across EVERY line it occupies — what a tile badge shows.
   *
   * Once add-ons exist "the quantity of product X" has no single answer, so the tile shows the sum:
   * a guest with two cheese pizzas and one bacon pizza has ordered three of that dish. Use
   * `getLineQuantity` wherever one specific line is meant.
   */
  getProductQuantity(productId: number): number {
    return this.cartItems.getValue()
      .filter(item => item.id === productId)
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Quantity of the ONE line matching this exact selection — what the product sheet's stepper
   * reads, because add / update / remove all act on a single line.
   */
  getLineQuantity(productId: number, supplements?: CartSupplement[]): number {
    const lineKey = cartLineKey(productId, normalizeCartSupplements(supplements));
    return this.cartItems.getValue().find(item => this.getLineKey(item) === lineKey)?.quantity ?? 0;
  }

  /** The distinct lines a product occupies, in the one deterministic order every surface uses. */
  getProductLines(productId: number): CartItem[] {
    return productCartLines(this.cartItems.getValue(), productId);
  }

  /**
   * The line a product-keyed QUANTITY call should act on, or null when there is no unambiguous
   * answer.
   *
   * One line (the only possibility at a venue without add-ons) is that line. Several lines mean the
   * caller — a tile or a sheet that has not been taught about lines — cannot have meant any
   * particular one, and this returns null so the call is inert.
   *
   * There is deliberately no "fall back to the plain `<id>|` line" branch. It reads like a safe
   * default and is not one: every caller here passes an ABSOLUTE quantity it computed from the row
   * the guest actually pointed at, so retargeting that number at a different row writes a value
   * belonging to one line onto another. Concretely, with Pizza+cheese ×3 and plain Pizza ×1, a `+`
   * on the cheese row calls `updateQuantity(id, 4)` and the PLAIN line goes 1 → 4: the cart jumps
   * from 4 units to 7 and the row the guest touched never moves. A dead button is recoverable; a
   * silently wrong quantity is not noticed until the order reaches the pass.
   *
   * Removal is NOT ambiguous in the same way and does not come through here — see `removeFromCart`.
   * Callers that know which line they mean use `updateLineQuantity` / `removeLine` with an explicit
   * key.
   */
  private resolveSingleLineKey(productId: number): string | null {
    const lines = this.getProductLines(productId);
    return lines.length === 1 ? this.getLineKey(lines[0]) : null;
  }

  /**
   * The canonical analytics source values, which these maps must treat as fixed points.
   *
   * Some call sites pass a screen name (`productDetails`, `most-ordered`) and others pass a value
   * that is already canonical — `updateLineQuantity` hands its `changeSource` straight to
   * `removeLine` when a stepper reaches zero, and that argument arrived pre-mapped as
   * `'product_details'` or `'order_details'`. Without this set those fell through every branch to
   * the `'menu'` default, so a removal from the cart screen was reported as a removal from the menu.
   */
  private static readonly CANONICAL_SOURCES = new Set(
    ['menu', 'product_details', 'order_details', 'search']
  );

  private mapAddSource(source: string): string {
    if (CartService.CANONICAL_SOURCES.has(source)) return source;
    if (source === 'productDetails') return 'product_details';
    if (source === 'order') return 'order_details';
    if (source === 'main' || source === 'topProducts' || source === 'most-ordered') return 'menu';
    return 'menu';
  }

  private mapRemoveSource(source: string): string {
    if (CartService.CANONICAL_SOURCES.has(source)) return source;
    if (source === 'productDetails') return 'product_details';
    if (source === 'order' || source === 'orderDetails') return 'order_details';
    if (source === 'main' || source === 'topProducts' || source === 'most-ordered') return 'menu';
    return 'menu';
  }

  private updateTotal() {
    // Add-on inclusive. This total feeds the floating cart, the order-now bar AND both
    // second-currency conversions, so leaving add-ons out under-reports in several places at once.
    const total = this.cartItems.getValue().reduce(
      (sum, item) => sum + cartLineTotal(item.price, item.quantity, item.supplements),
      0
    );
    this._total.next(total);
  }

  clearCart() {
    const currentItems = this.cartItems.getValue();
    if (currentItems.length > 0) {
      this.queueSync({
        line_items: this.formatLineItems(currentItems, 'delete')
      });
    }

    this.cartItems.next([]);
    this._total.next(0);
    this.resetPromoState();
    this.saveCartToStorage();
  }

  clearCartLocally() {
    this.cartItems.next([]);
    this._total.next(0);
    this.resetPromoState();
    this.saveCartToStorage();
  }

  /**
   * Forget the applied code and its discount.
   *
   * Local only — it deliberately does NOT sync a clear to the server. Both callers are
   * emptying the cart, and an order with no lines has nothing to discount; the code is
   * released server-side when the order is cancelled or abandoned.
   */
  private resetPromoState(): void {
    this.pendingPromoCode = undefined;
    this._appliedPromoCode.next(null);
    this._discountTotal.next(0);
    // The tip lives and dies with the same cart, so it is forgotten at the same moments.
    this.pendingTip = undefined;
    this._tip.next(null);
    // So does the delivery fee. The guest's pin is remembered by the Order-now sheet, which
    // sends it to the next order when they pick delivery again.
    this.pendingDeliveryLocation = undefined;
    this._deliveryFee.next(null);
    this._deliveryQuote.next(null);
    this._deliveryPricing.next(false);
  }
}


